import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/apiClient";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileSpreadsheet, FileText, Loader2 } from "lucide-react";

const REPORTS = {
  enrollments: { label: "Enrollment", filters: ["branch", "academic"] },
  finance: { label: "Finance", filters: ["branch", "dates"] },
  placement: { label: "Placement", filters: ["placed"] },
};
const ACADEMIC = ["Enrolled", "In Progress", "Completed", "Dropped", "On Hold"];

function ReportView({ reportKey }) {
  const cfg = REPORTS[reportKey];
  const [data, setData] = useState(null);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [f, setF] = useState({ branch_id: "all", status: "all", placed: "all", date_from: "", date_to: "" });

  useEffect(() => { if (cfg.filters.includes("branch")) api.get("/branches").then(({ data }) => setBranches(data || [])); }, [cfg]);

  const params = useCallback(() => {
    const p = {};
    if (cfg.filters.includes("branch") && f.branch_id !== "all") p.branch_id = f.branch_id;
    if (cfg.filters.includes("academic") && f.status !== "all") p.status = f.status;
    if (cfg.filters.includes("placed") && f.placed !== "all") p.placed = f.placed;
    if (cfg.filters.includes("dates")) { if (f.date_from) p.date_from = f.date_from; if (f.date_to) p.date_to = f.date_to; }
    return p;
  }, [cfg, f]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/${reportKey}`, { params: { format: "json", ...params() } });
      setData(data);
    } finally { setLoading(false); }
  }, [reportKey, params]);

  useEffect(() => { load(); }, [load]);

  const download = async (fmt) => {
    setDownloading(fmt);
    try {
      const res = await api.get(`/reports/${reportKey}`, { params: { format: fmt, ...params() }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${reportKey}_report.${fmt === "excel" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} exported`);
    } catch { toast.error("Export failed"); }
    finally { setDownloading(""); }
  };

  const headers = data?.headers || [];
  const rows = data?.rows || [];

  return (
    <div data-testid={`report-${reportKey}`}>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-2">
          {cfg.filters.includes("branch") && (
            <Select value={f.branch_id} onValueChange={(v) => setF((s) => ({ ...s, branch_id: v }))}>
              <SelectTrigger className="h-9 w-44" data-testid="report-branch-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All branches</SelectItem>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {cfg.filters.includes("academic") && (
            <Select value={f.status} onValueChange={(v) => setF((s) => ({ ...s, status: v }))}>
              <SelectTrigger className="h-9 w-40" data-testid="report-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All statuses</SelectItem>{ACADEMIC.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {cfg.filters.includes("placed") && (
            <Select value={f.placed} onValueChange={(v) => setF((s) => ({ ...s, placed: v }))}>
              <SelectTrigger className="h-9 w-36" data-testid="report-placed-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="true">Placed</SelectItem><SelectItem value="false">Not placed</SelectItem></SelectContent>
            </Select>
          )}
          {cfg.filters.includes("dates") && (
            <>
              <div><label className="text-xs text-muted-foreground">From</label><Input type="date" className="h-9" value={f.date_from} onChange={(e) => setF((s) => ({ ...s, date_from: e.target.value }))} /></div>
              <div><label className="text-xs text-muted-foreground">To</label><Input type="date" className="h-9" value={f.date_to} onChange={(e) => setF((s) => ({ ...s, date_to: e.target.value }))} /></div>
            </>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => download("excel")} disabled={!!downloading} data-testid={`export-excel-${reportKey}`}>{downloading === "excel" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-1.5 h-4 w-4" />} Excel</Button>
          <Button onClick={() => download("pdf")} disabled={!!downloading} data-testid={`export-pdf-${reportKey}`}>{downloading === "pdf" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileText className="mr-1.5 h-4 w-4" />} PDF</Button>
        </div>
      </div>

      <p className="mb-2 text-sm text-muted-foreground">{data?.total ?? 0} records {rows.length < (data?.total ?? 0) ? `(showing first ${rows.length})` : ""}</p>
      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <Table>
          <TableHeader><TableRow>{headers.map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={headers.length || 1} className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-primary" /></TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={headers.length || 1} className="py-8 text-center text-muted-foreground">No records.</TableCell></TableRow>}
            {!loading && rows.map((r, i) => (
              <TableRow key={i}>{headers.map((h) => <TableCell key={h} className="whitespace-nowrap text-sm">{String(r[h] ?? "")}</TableCell>)}</TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function Reports() {
  return (
    <div data-testid="reports-page">
      <PageHeader title="Reports" subtitle="Export enrollment, finance and placement data." />
      <Tabs defaultValue="enrollments">
        <TabsList className="mb-6">
          {Object.entries(REPORTS).map(([k, v]) => <TabsTrigger key={k} value={k} data-testid={`report-tab-${k}`}>{v.label}</TabsTrigger>)}
        </TabsList>
        {Object.keys(REPORTS).map((k) => <TabsContent key={k} value={k}><ReportView reportKey={k} /></TabsContent>)}
      </Tabs>
    </div>
  );
}
